import { useEffect } from "react";

/**
 * Adds the `.is-visible` class to any `.reveal` element as it scrolls into view.
 *
 * Uses an IntersectionObserver to fade items in, plus a MutationObserver so that
 * elements added later (e.g. after async API data loads and re-renders sections
 * like Amenities, Testimonials, FAQs) are still observed and become visible.
 */
export function useReveal() {
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -5% 0px" }
    );

    const observeAll = () => {
      document.querySelectorAll(".reveal:not(.is-visible)").forEach((el) => {
        // IntersectionObserver internally deduplicates the same element.
        io.observe(el);
      });
    };

    observeAll();

    // Watch the DOM for new `.reveal` elements added by late-rendering sections.
    const mo = new MutationObserver(() => observeAll());
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, []);
}
